/* eslint-disable react/no-unstable-nested-components */
import { createContext, lazy, Suspense, useEffect, useMemo, useRef } from 'react';

import { Box, Image } from '@chakra-ui/react';
import { AlertDialogComponent } from '@mesh.ai/rabbit';
import '@mesh.ai/rabbit/dist/rabbit.css';
import * as microsoftTeams from '@microsoft/teams-js';
import * as Sentry from '@sentry/react';
import { CreateViolationDrawer } from 'Deer/components/DisciplinaryAction/BottomDrawers/CreateViolationDrawer/CreateViolationDrawer';
import { ViewViolationDrawer } from 'Deer/components/DisciplinaryAction/BottomDrawers/ViewViolationDrawer';
import { staticRoutes } from 'Deer/constants/routes';
import { useAuthentication } from 'Deer/hooks/auth/useAuthentication';
import { useDeerRabbitRedirection } from 'Deer/hooks/useDeerRabbitRedirection/useDeerRabbitRedirection';
import { useLoggedInUsersAccessesQuery } from 'Deer/hooks/useLoggedInUsersAccessesQuery';
import { setEmployeeGoalsAccessPrivilegesAction } from 'actions/employeeGoalsAccessPrivilegesActions';
import { setEnabledFeatureFlagsAction } from 'actions/enabledFeatureFlagsActions';
import { authenticateAction, setUserTimeZoneAction, updateLoggedInEmployeeAction } from 'actions/loggedInEmployee';
import { addMSTeamsContext } from 'actions/msTeamsActions';
import { addOrgDataAction } from 'actions/orgData';
import { NumberFilterModule, TextFilterModule } from 'ag-grid-community';
import {
  AllCommunityModule,
  AllEnterpriseModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  ModuleRegistry,
  provideGlobalGridOptions,
  RowGroupingModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  TooltipModule,
} from 'ag-grid-enterprise';
import AdminOrManagerRoute from 'components/AdminOrManagerRoute';
import { CompetencyDrawer } from 'components/Develop/CompetencyDrawer';
import ErrorBoundary from 'components/ErrorBoundary';
import { GoalsWeightProvider } from 'components/GoalsWeight/GoalsWeightContext';
import GoalsWeightSidepanelV1 from 'components/GoalsWeight/GoalsWeightSidepanelV1';
import HelloBar from 'components/HelloBar';
import { MobileLogoutButton } from 'components/MobileLogoutButton';
import KeyResultPromptModal from 'components/ObjectiveTagList/KeyResultPromptModal';
import ObjectivePromptModal from 'components/ObjectiveTagList/ObjectivePromptModal';
import PrivateRoute from 'components/PrivateRoute';
import SnackbarContainer from 'components/SnackbarContainer';
import { ViewsWrapper } from 'components/Views/ViewsWrapper';
// import { RouteMatchSidebarNavContainer } from 'components/RouteMatchSidebarNavContainer/RouteMatchSidebarNavContainer';
import Header from 'components/header/Header';
import { useIsSubDivScrollPage } from 'components/hooks/useIsSubDivScrollPage';
import useLocalStorage from 'components/hooks/useLocalStorage';
import { MatchedRouteProvider } from 'components/hooks/useMatchedRoute';
import { useParsedCohortQueryParam } from 'components/hooks/useParsedCohortQueryParam';
import { parseUrl, stringify } from 'query-string';
import { connect, useDispatch, useSelector } from 'react-redux';
import { matchPath, Redirect, Route, Switch, useLocation, useRouteMatch, withRouter } from 'react-router-dom';
import { useUnsavedDataAlertProps } from 'reducers/unsavedDataAlertSlice';
import { useAppRefreshCount } from 'reducers/viewsSlice';
import MsTeamsApp from 'screens/MSTeams';
import MSTeamsLogin from 'screens/MSTeams/Login';
import MSTeamLoginFinal from 'screens/MSTeams/Login/FinalCall';
import QuestionDetailsSidePanel from 'screens/admin/common/QuestionDetailsSidePanel';
import { CohortCreationContextProvider } from 'screens/common/CohortCreation/CohortSidePanelContext';
import { CohortCreationSidePanelControlProvider } from 'screens/common/CohortCreation/CohortSidePanelControlContext';
import CreateKRPanel from 'screens/globalDrawers/KR/CreateKRPanel';
import { CreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer';
import { isExploringDemoSelector, selectIsAuthenticated, selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import styled, { css } from 'styled-components';
import {
  checkNewNavEnabled,
  checkNewNavPreviewFeatureEnabled,
  checkPersonaInsightsEnabled,
  checkVisierAnalyticsEnabled,
} from 'utils/helpers/featureFlags';
// import { checkDisciplinaryModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsAnyModuleAdmin, checkUserIsHRISManager } from 'utils/helpers/rolesAccess';
import { retry } from 'utils/retry';

import LogoGifWithoutLabel from 'assets/gifs/logoWithoutLabel.gif';
import NetchexLoader from 'assets/gifs/netchexLoader.gif';

import {
  DEMO_APP_DOMAIN,
  FEATURE_KEYS,
  MARKETING_SITE,
  PROD_LOGIN_DOMAIN,
  SIGN_UP_ROUTES,
} from 'constants/ProjectConstants';
import { getQueryParams, getRouteByLoginEvent, goToDevEnvLoginPage, goToProdAppDomain } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import {
  getUsefulTrackingDataFromLoggedInEmployee,
  setMixpanelUserId,
  setMixpanelUserProperties,
} from 'constants/mixpanel';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { cohortBasedPageViewCheck } from 'constants/mixpanelHelpers/commonEventHelpers';
import { registerUser } from 'constants/moengage';
import { isNetchexLogin } from 'constants/netChexConstants';
import { rabbitRedirectCheck } from 'constants/redirectHelpers';
import { routes } from 'constants/routes';
import { routesForTracking } from 'constants/routesForTracking';

/* eslint-disable-next-line */

import { setUserTimeZone } from 'services/employee';
import { showErrorSnackbar } from 'services/snackbar';

import './polyfills';

ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  TooltipModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  ColumnMenuModule,
  ContextMenuModule,
  TextFilterModule,
  NumberFilterModule,
]);

provideGlobalGridOptions({ theme: 'legacy' });

const HeaderAndContentContainer = styled.div`
  position: relative;
  height: 100%;
  overflow-x: hidden;
  // overflow-y: ${(props) => (props.isSubDivScrollPage ? 'hidden' : 'auto')};
  flex-grow: 1;
`;

const PageContainer = styled.div`
  position: relative;
  ${(props) =>
    props.isAuthenticated &&
    !props.isSignUpRoute &&
    css`
      height: calc(100vh - ${props.isEnableNetchexEmployeeNavbar ? '0px' : '92px'});
      margin-top: ${props.isEnableNetchexEmployeeNavbar ? '0px' : '92px'};
    `}
  ${(props) =>
    props.isAuthenticated &&
    !props.isSignUpRoute &&
    props.isExploringDemo &&
    css`
      margin-top: 6vh;
      height: 88vh;
    `}
`;

// Lazy import components

const Login = lazy(() => retry(() => import('screens/Login')));
const NonAuthenticatedEmailChart = lazy(() =>
  retry(() =>
    import('screens/NonAuthenticatedChartGeneration/NonAuthenticatedEmailChart').then((module) => ({
      default: module.NonAuthenticatedEmailChart,
    }))
  )
);
const NonAuthenticatedNetchexMessage = lazy(() =>
  retry(() =>
    import('screens/NonAuthenticatedNetchexMessage/NonAuthenticatedNetchexMessage').then((module) => ({
      default: module.NonAuthenticatedNetchexMessage,
    }))
  )
);
const Home = lazy(() => retry(() => import('screens/home/Home')));
const Profile = lazy(() => retry(() => import('screens/Profile')));
const Admin = lazy(() => retry(() => import('screens/admin')));
const ModuleSettings = lazy(() =>
  retry(() => import('screens/ModuleSettings')).then((module) => ({
    default: module.ModuleSettings,
  }))
);
const TeamSummary = lazy(() => retry(() => import('screens/teamSummary')));
const Goals = lazy(() => retry(() => import('screens/Team/Rabbit/Goals')));
// const Team = lazy(() => retry(() => import('screens/Team')));
// const RabbitTeam = lazy(() => retry(() => import('screens/Team/Rabbit')));
const PerformanceReviewPage = lazy(() => retry(() => import('screens/performanceReview')));
const Engagement = lazy(() => retry(() => import('screens/Engagement')));
const DisciplinaryActions = lazy(() => retry(() => import('screens/DisciplinaryActions')));
const Initiative = lazy(() => retry(() => import('screens/Initiatives')));
const LevelUpPage = lazy(() =>
  retry(() => import('screens/Levelup')).then((module) => ({
    default: module.LevelUpPage,
  }))
);
const LevelUpCF = lazy(() =>
  retry(() => import('screens/Levelup/LevelUpCF')).then((module) => ({
    default: module.LevelUpCF,
  }))
);
const LevelUpMyTeam = lazy(() =>
  retry(() => import('screens/Levelup/LevelUpMyTeam')).then((module) => ({
    default: module.LevelUpMyTeam,
  }))
);
const OneOnOnePage = lazy(() => retry(() => import('screens/oneOnOne')));
const NotFound404 = lazy(() => retry(() => import('screens/NotFound404')));
const PersonaInsights = lazy(() =>
  retry(() => import('screens/personaInsights')).then((module) => ({
    default: module.PersonaInsights,
  }))
);
const EmbeddedAnalytics = lazy(() => retry(() => import('screens/EmbeddedAnalytics')));
const EmbeddedAnalyticsIframe = lazy(() => retry(() => import('screens/EmbeddedAnalyticsIframe')));
const EngagementTemplate = lazy(() => retry(() => import('screens/admin/Engagement/EngagementTemplate')));
const ReviewTemplate = lazy(() => retry(() => import('screens/admin/Reviews/ReviewTemplate')));
const CheckInTemplate = lazy(() => retry(() => import('screens/admin/CheckIn/CheckInTemplates/CheckInTemplateForm')));
const InitiativeCreationPanel = lazy(() =>
  retry(() => import('components/RabbitInitiativeCreationPanel/InitiativeCreationPanel'))
);
const InitiativeSidePanel = lazy(() => retry(() => import('components/RabbitInitiativeSidePanel')));
const OKRCreationPanel = lazy(() => retry(() => import('components/RabbitOKRCreationPanel/OKRCreationPanel')));
const OKRSidePanel = lazy(() => retry(() => import('components/RabbitOKRSidePanel')));
const RealignModalRabbit = lazy(() =>
  retry(() =>
    import('components/RealignModal/RealignModalRabbit').then((module) => ({
      default: module.RealignModalRabbit,
    }))
  )
);
const ImagePreviewContainer = lazy(() => retry(() => import('components/ImagePreviewContainer')));
const CreateSubObjectiveModal = lazy(() => retry(() => import('screens/common/createSubObjectiveModal')));
const ObjectiveCreationModal = lazy(() => retry(() => import('components/ObjectiveCreationModal')));
const ConfirmCohortBaseResetModal = lazy(() =>
  retry(() => import('screens/common/CohortCreation/ConfirmCohortBaseResetModal'))
);
const GifModal = lazy(() => retry(() => import('components/Gif/GifModal')));
const DatePickerComponentWithRedux = lazy(() => retry(() => import('components/DatePickerComponentWithRedux')));
const CustomDateTimePickerModalWithRedux = lazy(() =>
  retry(() => import('components/CustomDateTimePickerModalWithRedux'))
);
const ContinuousFeedbackSidebar = lazy(() =>
  retry(() => import('screens/performanceReview/components/ContinuousFeedback'))
);
const NominatePeersSidePanelV1 = lazy(() =>
  retry(() =>
    import('screens/performanceReview/components/nominatePeersSidePanelV1/NominatePeersSidePanelV1').then((module) => ({
      default: module.NominatePeersSidePanelV1,
    }))
  )
);

const CalibrationViewSidepanel = lazy(() =>
  retry(() =>
    import(
      'screens/performanceReview/manageAndInsights/TalentPlanning/CalibrationViewSidepanel/CalibrationViewSidepanel'
    ).then((module) => ({
      default: module.CalibrationViewSidepanel,
    }))
  )
);

const ReviewManageSidepanel = lazy(() =>
  retry(() =>
    import('screens/performanceReview/manageAndInsights/ReviewManage/reviewManageSidepanel/ReviewManageSidepanel').then(
      (module) => ({
        default: module.ReviewManageSidepanel,
      })
    )
  )
);

const RouteMatchSidebarNavContainer = lazy(() =>
  retry(() => import('components/RouteMatchSidebarNavContainer/RouteMatchSidebarNavContainer')).then((module) => ({
    default: module.RouteMatchSidebarNavContainer,
  }))
);

const GoalBulkUploadPanel = lazy(() =>
  retry(() => import('Deer/components/Goals/BulkUploadPanels/GoalBulkUploadPanel')).then((module) => ({
    default: module.GoalBulkUploadPanel,
  }))
);

const ObjectiveBulkUploadPanel = lazy(() =>
  retry(() => import('Deer/components/Goals/BulkUploadPanels/ObjectiveBulkUploadPanel')).then((module) => ({
    default: module.ObjectiveBulkUploadPanel,
  }))
);

// modules which have been added back
const MSTeamsHeader = lazy(() => retry(() => import('components/header/MSTeamsHeader')));
const DateTimePickerComponentWithRedux = lazy(() => retry(() => import('components/DateTimePickerComponentWithRedux')));
const TasksListContainerMobile = lazy(() => retry(() => import('screens/home/TasksListContainer.mobile')));
const EmailRegister = lazy(() => retry(() => import('screens/SignUp/emailRegister')));
const ForgotPassword = lazy(() => retry(() => import('screens/SignUp/forgotPassword')));
const ForgotPasswordConfirmation = lazy(() => retry(() => import('screens/SignUp/forgotPasswordConfirmation')));
const Invite = lazy(() => retry(() => import('screens/SignUp/invite')));
const ProfileSetup = lazy(() => retry(() => import('screens/SignUp/profileSetup')));
const SelectDepartment = lazy(() => retry(() => import('screens/SignUp/selectDepartment')));
const SelectTeam = lazy(() => retry(() => import('screens/SignUp/selectTeam')));
const ValidateEmail = lazy(() => retry(() => import('screens/SignUp/validateEmail')));
const GmailApp = lazy(() => retry(() => import('screens/Gmail/src/App')));
const MSTeamsConfig = lazy(() => retry(() => import('screens/MSTeams/Config')));
const MSTeamsConnectorConfig = lazy(() => retry(() => import('screens/MSTeams/Config/ConnectorConfig')));
const OutlookApp = lazy(() => retry(() => import('screens/Outlook/src/App')));
const OutlookLogin = lazy(() => retry(() => import('screens/Outlook/src/OutlookLogin')));
const SelectOrganization = lazy(() => retry(() => import('screens/Login/SelectOrganization')));
const DriverDetail = lazy(() => retry(() => import('screens/DriverDetail')));
const MeshIn = lazy(() => retry(() => import('screens/meshIn/MeshIn')));
const MeshUp = lazy(() => retry(() => import('screens/meshUp/MeshUp')));
const StrengthDetail = lazy(() => retry(() => import('screens/StrengthDetail')));
const AnalyticsPage = lazy(() => retry(() => import('screens/analytics')));
// const DeliverableDetail = lazy(() => retry(() => import('screens/DeliverableDetail')));
const DiscoverDetail = lazy(() => retry(() => import('screens/DiscoverDetail')));
const MeshMoney = lazy(() => retry(() => import('screens/MeshMoney')));
const SurveyDetail = lazy(() => retry(() => import('screens/SurveyDetail')));
const SearchWithBackButton = lazy(() => retry(() => import('components/SearchWithBackButton')));
// const TagDetail = lazy(() => retry(() => import('screens/TagDetail')));

const DeerApp = lazy(() => retry(() => import('Deer')));

export const ScrollContext = createContext(null);
// TODO: shift it to a reusable hook
function addSentryScope(employee) {
  try {
    if (employee && employee.id) {
      const { id, gender, displayName, email } = employee;
      Sentry.getCurrentScope().setUser({
        id,
        gender,
        email,
        displayName,
      });
    }
  } catch (err) {
    Sentry.captureException(err);
  }
}

// eslint-disable-next-line consistent-return
function goToSignUpPageOrHomePage({ res, push, path, queryString, replaceState }) {
  // returns signup routes according to the last singup event that user performed
  const route = getRouteByLoginEvent(res.employee.lastUserEvent, routes.home);
  // If it's not home page, just redirect to that page
  // because we need the user to restart from the previous signup step
  if (route !== routes.home) {
    push(route);
    return false;
  }
  // If it is home page, that means signup is done and current route is
  // trying to go to a signup page, so we are redirecting the user to the home page
  if (route === routes.home && [routes.home, ...SIGN_UP_ROUTES].includes(path)) {
    const redirectRoute = routes.home + queryString || '';
    replaceState(redirectRoute);
    return false;
  }
}

function App({
  location,
  history,
  isAuthenticated,
  setAuthenticated,
  setLoggedInUser,
  setOrgData,
  loggedInEmployee,
  msTeamsContext,
  // eslint-disable-next-line no-shadow
  addMSTeamsContext,
  setEnabledFeatureFlags,
  setEmployeeGoalsAccessPrivileges,
}) {
  const { organizationId, organizationName, loggedInEmployeeId, loggedInEmployeeEmail, fullName } =
    getUsefulTrackingDataFromLoggedInEmployee(loggedInEmployee);
  const isExploringDemo = useSelector(isExploringDemoSelector);
  const unsavedAlertProps = useUnsavedDataAlertProps();
  const isTeamsVisitor = msTeamsContext && Object.keys(msTeamsContext).length > 0;
  const isProdLoginDomain = window.location.host === PROD_LOGIN_DOMAIN;
  const isDemoDomain = window.location.host === DEMO_APP_DOMAIN;
  const isLogoutPath = window.location.pathname === '/logout';
  const parsedCohortQueryParams = useParsedCohortQueryParam();
  const appRefreshCount = useAppRefreshCount();

  const currentBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isTimezoneDifferent = currentBrowserTimezone !== loggedInEmployee?.timezone;

  const dispatch = useDispatch();
  const { loadingComplete } = useLoggedInUsersAccessesQuery();

  const isSignUpRoute = SIGN_UP_ROUTES.includes(history.location.pathname);

  const { isExact: isNonAuthChartRoute } = useRouteMatch(routes.nonAuthCharts.nonAuthEmailChart) || {};
  const { isExact: isNetchexNonAuthMessageRoute } = useRouteMatch(routes.nonAuthNetchexMessage) || {};

  const isEmbeddedAnalyticsIframeRoute = routes.embeddedAnalytics.connectIframe === history.location.pathname;

  const isSubDivScrollPage = useIsSubDivScrollPage();

  const { search: queryString } = useLocation();

  const {
    prompt,
    demoUserEmail: queryDemoUserEmail = undefined,
    overrideFeatureFlags = null,
  } = getQueryParams(queryString);

  if (process.env.PUBLIC_APP_ENABLE_FEATURE_FLAG_OVERRIDES === 'true' && overrideFeatureFlags) {
    window.sessionStorage.setItem('overrideFeatureFlags', overrideFeatureFlags);
  }
  // this demoUserEmail doesn't actually exist in backend, but is just captured from query parameter
  // that's why this tricky code is required.
  // basically if we have the demoUser email in query parameter we make different api call without authentication
  // and send this email in analytics
  const [demoUserEmailLS] = useLocalStorage(localStorageKeys.demoUserEmail, queryDemoUserEmail, true);
  // this boolean is for first time login only when demoUserEmail query param is supplied, because we need to call separate API
  // the authenticate calls after that will still be from the demoUser account authToken

  const isDemoUserLogin = isDemoDomain && !!queryDemoUserEmail;

  const isMSTeamsUrl = prompt === 'msteams' || window.location.pathname.split('/')?.[1] === 'msteams';

  useEffect(() => {
    microsoftTeams.getContext((context) => {
      if (context && Object.keys(context).length > 0) {
        addMSTeamsContext(context);
        if (history.location.pathname === '/') {
          history.push(`${routes.msTeams.Final}?success=true`);
        }
      }
    });

    microsoftTeams.appInitialization.notifySuccess();
  }, [isAuthenticated]);

  useAuthentication({
    addSentryScope,
    goToSignUpPageOrHomePage,
    setAuthenticated,
    setEmployeeGoalsAccessPrivileges,
    setEnabledFeatureFlags,
    setLoggedInUser,
    setOrgData,
    isNonAuthRoute: isNonAuthChartRoute || isNetchexNonAuthMessageRoute,
  });

  useEffect(() => {
    if (isTimezoneDifferent && isAuthenticated) {
      setUserTimeZone({
        employeeId: loggedInEmployeeId,
        timezone: currentBrowserTimezone,
      }).then(() => {
        dispatch(setUserTimeZoneAction(currentBrowserTimezone));
      });
    }
  }, [isTimezoneDifferent, isAuthenticated, history.location.pathname]);

  useEffect(() => {
    if (!loggedInEmployeeId || !organizationId) return;

    // Add MS clarity user information
    window?.clarity?.('identify', loggedInEmployeeId);
    window?.clarity?.('set', 'orgId', organizationId);

    // becasue we don't want to enable tracking in  dev and staging env
    // passed in deployscript
    // we are disabling this in deployscript
    if (process.env.PUBLIC_APP_ENABLE_TRACKING !== 'true') return;
    if (!process.env.PUBLIC_APP_MIXPANEL_KEY) return;

    // if (!organizationId) return;

    const path = history.location.pathname;
    // eslint-disable-next-line no-shadow
    let route = Object.values(routesForTracking).find((route) => matchPath(path, { path: route.path, exact: true }));
    route = cohortBasedPageViewCheck({ route, parsedCohortQueryParams });
    const pageName = route ? route.pageName : null;
    const type = route ? (route.type ?? null) : null;

    // user is not authenticated and trying to access home, we don't need this data, so returning
    if (pageName === 'home' && !loggedInEmployeeId) return;
    if (loggedInEmployeeId) setMixpanelUserId(loggedInEmployeeId);

    if (pageName) {
      const userProperties = {};
      if (organizationId) userProperties.organizationId = organizationId;
      if (organizationName) userProperties.organizationName = organizationName;
      userProperties.designation = loggedInEmployee.designation ?? '';
      if (loggedInEmployeeEmail) userProperties.email = loggedInEmployeeEmail;
      if (fullName) userProperties.fullName = fullName;
      userProperties.onNewNav = !!checkNewNavEnabled();
      if (Object.keys(userProperties).length > 0) setMixpanelUserProperties(userProperties);

      const eventProperties = { page: pageName };
      if (type) {
        eventProperties.type = type;
      }
      if (demoUserEmailLS) {
        eventProperties.demoUserEmail = demoUserEmailLS;
      }

      sendPageViewEvent({ eventProperties });
    }
  }, [history.location.pathname, organizationId, loggedInEmployeeId, organizationName, loggedInEmployeeEmail]);
  const scrollParentRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && loggedInEmployeeEmail) registerUser(loggedInEmployeeEmail);
  }, [isAuthenticated, loggedInEmployeeEmail]);

  // commenting out redirection if user hasn't completed onboarding
  // in new onboarding tooltips will be shown day-wise day0, day 7, day 14 etc
  // and unlike previous tooltips not all of them will be triggered from home page

  // useEffect(() => {
  //   if (
  //     loggedInEmployee &&
  //     !hasEmployeeCompletedOnboarding(loggedInEmployee) &&
  //     // in case of `goalTasks` onboarding step, the url is of selected onboarding goal. Don't redirect to home in that case
  //     !isEmployeeOnGoalTasksOnboardingStep(loggedInEmployee)
  //   ) {
  //     history.push(routes.home);
  //   }
  // }, [loggedInEmployee]);

  // using useMemo to memoize this check only on url change.
  const rabbitTargetURL = useMemo(() => rabbitRedirectCheck({ location }), [location.pathname]);
  if (rabbitTargetURL && !checkNewNavEnabled() && isAuthenticated) {
    history.push(rabbitTargetURL);
  }

  useEffect(() => {
    if (isAuthenticated) {
      LicenseManager.setLicenseKey(process.env.PUBLIC_APP_AG_GRID_LICENSE_KEY);
    }
  }, [isAuthenticated]);

  useDeerRabbitRedirection();

  useEffect(() => {
    if (isAuthenticated) {
      if (location.pathname?.includes('/calendar/verify/')) {
        const calendarType = location.pathname.split('/calendar/verify/')[1] ?? '';
        const { query } = parseUrl(window.location.href);
        const search = stringify({
          ...query,
          calendarType,
          ...(checkNewNavEnabled() ? {} : { module: 'oneOnOne', activeTabId: 'calendar' }),
        });
        const pathname = checkNewNavEnabled() ? staticRoutes['my-data']['my-settings'].calendar : '/moduleSettings';

        history.replace({
          pathname,
          search,
        });
      }
    }
  }, [isAuthenticated]);

  // prod conditions
  // if (isProdAppDomain && isLogoutPath && !isMSTeamsUrl) {
  //   const { w } = getQueryParams(queryString);
  //   goToProdLoginDomain({ queryParams: { w, action: 'logout' } });
  //   return null;
  // }
  // if (isProdAppDomain && isAuthenticated === false && !isMSTeamsUrl) {
  //   goToProdLoginDomain({});
  //   return null;
  // }

  if (queryDemoUserEmail) {
    history.push(routes.home);
  }
  if (isDemoDomain && !demoUserEmailLS) {
    window.location.href = MARKETING_SITE;
  }

  if (isProdLoginDomain && isAuthenticated && !isMSTeamsUrl) {
    goToProdAppDomain();
    return null;
  }
  // if (isProdLoginDomain && !isAuthenticated && !isMSTeamsUrl) {
  //   // const { w: org, code } = getQueryParams(queryString);

  //   return (
  //     <>
  //       <Login />
  //       <SnackbarContainer />
  //     </>
  //   );
  // }

  // non prod conditions
  if (isLogoutPath && !isMSTeamsUrl) {
    const { email } = getQueryParams(queryString);
    goToDevEnvLoginPage({ queryParams: { email, action: 'logout' } });
    return null;
  }

  // temp on non-rabbit, to redirect feedback urls to review landing page
  // if (location.pathname === '/feedback') {
  //   history.replace({
  //     pathname: routes.performanceReview.index,
  //     search: location.search
  //   });
  // }

  // if (isAuthenticated !== null && window.location.host === PROD_LOGIN_DOMAIN) {
  //   return <Login />;
  // }

  if (isNonAuthChartRoute) {
    return <Route path={routes.nonAuthCharts.nonAuthEmailChart} component={NonAuthenticatedEmailChart} />;
  }

  if (isNetchexNonAuthMessageRoute) {
    return <Route path={routes.nonAuthNetchexMessage} component={NonAuthenticatedNetchexMessage} />;
  }

  if (!loadingComplete) return null;

  const RightAppContent =
    isAuthenticated === null ? null : (
      // <EmployeeProvider>
      // <EmployeeContext.Consumer>
      //   {({ isAuthenticated }) => (
      <HeaderAndContentContainer ref={scrollParentRef} isSubDivScrollPage={isSubDivScrollPage}>
        <ScrollContext.Provider value={scrollParentRef}>
          {isAuthenticated &&
          !isSignUpRoute &&
          !isTeamsVisitor &&
          !isEmbeddedAnalyticsIframeRoute &&
          !isNetchexLogin ? (
            <Header />
          ) : null}

          {isAuthenticated && !isSignUpRoute && isTeamsVisitor && !isEmbeddedAnalyticsIframeRoute ? (
            <MSTeamsHeader />
          ) : null}
          {isAuthenticated && !isSignUpRoute && <HelloBar />}
          {/* {isAuthenticated && !isSignUpRoute && <JoyrideWrapper />} */}
          <Suspense
            fallback={
              <Box display="flex" width="100vw" height="100vh" justifyContent="center" alignItems="center">
                <Image w="100px" h="100px" src={isNetchexLogin ? NetchexLoader : LogoGifWithoutLabel} alt="Loader" />
              </Box>
            }
          >
            <GoalsWeightProvider>
              <PageContainer
                isExploringDemo={isExploringDemo}
                isAuthenticated={isAuthenticated}
                isSignUpRoute={isSignUpRoute}
                isTeamsVisitor={isTeamsVisitor}
                isEnableNetchexEmployeeNavbar={isNetchexLogin}
              >
                <Switch>
                  <Route path={routes.login} component={() => <Login />} exact />
                  <Route path={routes.loginWithRegion} component={() => <Login />} exact />
                  <Route path={routes.logout} component={() => <Login routeAction="logout" />} exact />
                  <Route path={routes.gmail} component={GmailApp} exact />
                  <Route path={routes.outlook.home} component={OutlookApp} exact />
                  <Route path={routes.outlook.login} component={OutlookLogin} exact />
                  <Route exact path={routes.emailRegister} component={EmailRegister} />
                  <Route exact path={routes.validateEmail} component={ValidateEmail} />
                  <Route exact path={routes.forgotPassword} component={ForgotPassword} />
                  <Route exact path={routes.forgotPasswordConfirmation} component={ForgotPasswordConfirmation} />
                  <Route exact path={routes.selectDepartment} component={SelectDepartment} />
                  <Route exact path={routes.selectOrganization} component={SelectOrganization} />
                  <Route exact path={routes.selectTeam} component={SelectTeam} />
                  <Route exact path={routes.invite} component={Invite} />
                  <Route exact path={routes.profileSetup} component={ProfileSetup} />
                  <PrivateRoute path={routes.surveyDetail} component={SurveyDetail} exact />
                  {/* <PrivateRoute path={routes.tagDetail} component={TagDetail} exact /> */}
                  <PrivateRoute
                    path={routes.strengthDetail}
                    component={StrengthDetail}
                    exact
                    featureKey={FEATURE_KEYS.FEEDBACK}
                  />
                  <PrivateRoute path={routes.driverDetail} component={DriverDetail} exact />
                  {/* <PrivateRoute path={routes.deliverableDetail} component={DeliverableDetail} exact /> */}
                  <PrivateRoute path={routes.discoverDetail} component={DiscoverDetail} exact />
                  <PrivateRoute
                    exact
                    path={routes.meshStore}
                    component={MeshMoney}
                    featureKey={FEATURE_KEYS.FEEDBACK}
                  />
                  <PrivateRoute exact path={routes.meshin} component={MeshIn} />
                  <PrivateRoute exact path={routes.meshup} component={MeshUp} />
                  <PrivateRoute path={routes.searchResults} component={SearchWithBackButton} exact />
                  <PrivateRoute exact path={routes.profile} component={Profile} />
                  <PrivateRoute exact path={routes.feedDetail} component={Home} />
                  <PrivateRoute exact path={routes.home} component={Home} />
                  <PrivateRoute exact path={routes.appHome} component={Home} />
                  <PrivateRoute path={routes.goals.index} component={Goals} />

                  <PrivateRoute path={routes.settings.index} component={Admin} />
                  <PrivateRoute path={routes.moduleSettings} component={ModuleSettings} />
                  <PrivateRoute exact path={routes.teamSummary} component={TeamSummary} />
                  <PrivateRoute exact path={routes.tasksSummary} component={TasksListContainerMobile} />
                  <PrivateRoute
                    path={routes.performanceReview.index}
                    component={PerformanceReviewPage}
                    featureKey={FEATURE_KEYS.REVIEW}
                  />

                  {/* TODO: Add routes for auth & goals */}
                  <Route exact path={routes.msTeams.Login} component={MSTeamsLogin} />
                  <Route exact path={routes.msTeams.Final} component={MSTeamLoginFinal} />
                  <PrivateRoute path={routes.msTeams.index} component={MsTeamsApp} />
                  {/* <MSTeamsRoute path={routes.msTeamsTeamHub} component={MSTeamsEmployeeTasksList} />
                <MSTeamsRoute path={routes.msTeams.Search} component={MSTeamsSearch} />
                <MSTeamsRoute
                  path={routes.msTeams.PerformanceReview.index}
                  component={PerformanceReviewPage}
                /> */}
                  <PrivateRoute exact path={routes.msTeams.TabConfig} component={MSTeamsConfig} />
                  <PrivateRoute exact path={routes.msTeams.ConnectorConfig} component={MSTeamsConnectorConfig} />
                  <PrivateRoute path={routes.analytics.index} component={AnalyticsPage} />
                  <PrivateRoute
                    path={routes.engagement.index}
                    component={Engagement}
                    featureKey={FEATURE_KEYS.ENGAGEMENT}
                  />

                  {/* No checks for demo - add checks for dev later */}
                  <PrivateRoute path={routes.disciplinaryActions.index} component={DisciplinaryActions} />

                  {checkPersonaInsightsEnabled() && (checkUserIsAnyModuleAdmin() || checkUserIsHRISManager()) && (
                    <PrivateRoute
                      path={routes.personaInsights.index}
                      component={PersonaInsights}
                      // featureKey={FEATURE_KEYS.ENGAGEMENT}
                    />
                  )}

                  <Route path={routes.embeddedAnalytics.connectIframe} component={EmbeddedAnalyticsIframe} exact />

                  {checkVisierAnalyticsEnabled() && (
                    <PrivateRoute path={routes.embeddedAnalytics.index} component={EmbeddedAnalytics} />
                  )}
                  <PrivateRoute
                    path={routes.initiative.index}
                    component={Initiative}
                    // featureKey={FEATURE_KEYS.INITIATIVE}
                  />
                  <PrivateRoute
                    path={routes.oneOnOne.index}
                    component={OneOnOnePage}
                    featureKey={FEATURE_KEYS.CHECK_IN}
                    isTeamsVisitor={isTeamsVisitor}
                  />
                  <PrivateRoute
                    path={routes.levelUp.index}
                    component={LevelUpPage}
                    featureKey={FEATURE_KEYS.LEVEL_UP}
                    exact
                  />
                  <PrivateRoute
                    path={routes.continuousFeedback.index}
                    component={LevelUpCF}
                    featureKey={FEATURE_KEYS.FEEDBACK} // <- to figure out with BE
                    exact
                  />
                  <PrivateRoute
                    path={routes.levelUp.myTeam}
                    component={LevelUpMyTeam}
                    featureKey={FEATURE_KEYS.LEVEL_UP}
                  />
                  <AdminOrManagerRoute
                    path={routes.template.review.index}
                    component={ReviewTemplate}
                    featureKey={FEATURE_KEYS.REVIEW}
                  />
                  <AdminOrManagerRoute
                    path={routes.template.checkIn.index}
                    component={CheckInTemplate}
                    featureKey={FEATURE_KEYS.CHECK_IN}
                  />
                  <AdminOrManagerRoute
                    path={routes.template.engagement.index}
                    component={EngagementTemplate}
                    featureKey={FEATURE_KEYS.ENGAGEMENT}
                  />
                  <Route component={NotFound404} />
                </Switch>
                <ImagePreviewContainer />
                <SnackbarContainer />
                {isAuthenticated ? (
                  <>
                    <OKRSidePanel />
                    <OKRCreationPanel />
                    <InitiativeSidePanel />
                    <InitiativeCreationPanel />
                  </>
                ) : null}
                {isAuthenticated ? <CreateOneOnOneDrawer /> : null}
                <CreateSubObjectiveModal />
                <ObjectiveCreationModal />
                <RealignModalRabbit />
                <ConfirmCohortBaseResetModal />
                <AlertDialogComponent
                  {...unsavedAlertProps}
                  key={unsavedAlertProps?.isUnsavedDataAlertDialogVisible}
                  isOpen={unsavedAlertProps?.isUnsavedDataAlertDialogVisible}
                  isTriggerRequired={false}
                />
                <GifModal />
                <DatePickerComponentWithRedux />
                <DateTimePickerComponentWithRedux />
                <CustomDateTimePickerModalWithRedux />
                <ContinuousFeedbackSidebar />
                <NominatePeersSidePanelV1 />
                <CalibrationViewSidepanel />
                <ReviewManageSidepanel />
                <GoalsWeightSidepanelV1 />

                <GoalBulkUploadPanel />
                <ObjectiveBulkUploadPanel />

                <MobileLogoutButton />
                <CreateViolationDrawer />
                <ViewViolationDrawer />

                {isAuthenticated ? (
                  <>
                    <QuestionDetailsSidePanel />
                    <CompetencyDrawer />
                    <ObjectivePromptModal />
                    <KeyResultPromptModal />

                    <CreateKRPanel />
                  </>
                ) : null}
                <CohortCreationContextProvider>{isAuthenticated && <ViewsWrapper />}</CohortCreationContextProvider>
              </PageContainer>
            </GoalsWeightProvider>
          </Suspense>
        </ScrollContext.Provider>
      </HeaderAndContentContainer>
    );

  if (isAuthenticated !== null && !isAuthenticated) {
    return <Redirect to={{ pathname: staticRoutes.login }} />;
  }

  return (
    <ErrorBoundary>
      <MatchedRouteProvider>
        <CohortCreationSidePanelControlProvider>
          {checkNewNavEnabled() || checkNewNavPreviewFeatureEnabled() ? (
            <DeerApp isAuthenticated={isAuthenticated} key={appRefreshCount} />
          ) : (
            <RouteMatchSidebarNavContainer rightContentChildren={RightAppContent} key={appRefreshCount} />
          )}
        </CohortCreationSidePanelControlProvider>
        <SnackbarContainer />
      </MatchedRouteProvider>
    </ErrorBoundary>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  loggedInEmployee: selectLoggedInEmployeeData(state),
  msTeamsContext: selectMSTeamsContext(state),
});

const mapDispatchToProps = (dispatch) => ({
  setAuthenticated: (isAuthenticated) => {
    dispatch(authenticateAction(isAuthenticated));
  },
  setLoggedInUser: (employee) => {
    dispatch(updateLoggedInEmployeeAction(employee));
  },
  setEnabledFeatureFlags: (enabledFeatureFlags) => {
    dispatch(setEnabledFeatureFlagsAction(enabledFeatureFlags));
  },
  setEmployeeGoalsAccessPrivileges: (accessPrivileges) => {
    dispatch(setEmployeeGoalsAccessPrivilegesAction(accessPrivileges));
  },
  setOrgData: (orgData) => {
    dispatch(addOrgDataAction(orgData));
  },
  dispatchShowErrorSnackbar: (message) => {
    dispatch(showErrorSnackbar(message));
  },
  addMSTeamsContext: (context) => {
    dispatch(addMSTeamsContext(context));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));
